/* eslint-disable @typescript-eslint/ban-ts-comment */
type OptionalId = string | number | null;

export type NestedItem<KChildren extends string = 'children', KId extends string = 'id'> = Partial<
  Record<KChildren, NestedItem<KChildren, KId>[]>
> &
  Record<KId, OptionalId>;

export type ParentNotated<TBase, KParent extends string = 'parentId'> = TBase &
  Record<KParent, OptionalId>;

type FilterFunction<TItem> = Parameters<Array<TItem>['filter']>[0];

// TODO: Type key params as keyof TItem

/**
 * Util for flattening an object's deep children
 *
 * DOES NOT REMOVE DUPLICATES, if items are many to many
 *
 * @param item - an object who's children you want to flatten
 * @param nestingKey - the key of the children, defaults to 'children'
 * @param flatList - an array to add the results to, defaults to `[]`
 * @returns a flat array of all the deep children
 */
export const getChildren = <
  TItem extends NestedItem<KChildren, KId>,
  KChildren extends string = 'children',
  KId extends string = 'id'
>(
  item: TItem,
  nestingKey: KChildren = 'children' as KChildren,
  idKey: KId = 'id' as KId,
  flatList: ParentNotated<NestedItem<KChildren, KId>>[] = []
): ParentNotated<TItem>[] => {
  const itemId = item[idKey];

  const children = item[nestingKey];
  if (!children) {
    return flatList as ParentNotated<TItem>[];
  }
  flatList.push(...children.map((child) => ({ ...child, parentId: itemId })));
  children.forEach((child) => getChildren(child, nestingKey, idKey, flatList));
  return flatList as ParentNotated<TItem>[];
};

export const getChildrenIds = <
  TItem extends NestedItem<KChildren, KId>,
  KChildren extends string = 'children',
  KId extends string = 'id'
>(
  item: TItem,
  nestingKey: KChildren = 'children' as KChildren,
  idKey: KId = 'id' as KId,
  flatList = []
) => {
  return getChildren<TItem, KChildren, KId>(item, nestingKey, idKey, flatList).map(
    (child) => child[idKey]
  );
};

/**
 * Util for flattening an array of tree objects, or a single tree object
 *
 * DOES NOT REMOVE DUPLICATES, if items are many to many
 *
 * @param tree - either an array of tree objects, or a single tree object
 * @param nestingKey - the key of the children, defaults to 'children'
 * @returns a flat array of all the deep children
 */
export const flattenTree = <
  TItem extends NestedItem<KChildren, KId>,
  KChildren extends string = 'children',
  KId extends string = 'id'
>(
  tree: TItem[] | TItem,
  nestingKey: KChildren = 'children' as KChildren,
  idKey: KId = 'id' as KId
) => {
  if (Array.isArray(tree)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nested: any = {
      [idKey]: null,
      [nestingKey]: tree,
    };

    return getChildren(nested, nestingKey) as ParentNotated<TItem>[];
  } else {
    // TS seems to think TItem doesn't match NestedItem, even tho it extends it
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return getChildren(tree as any, nestingKey) as ParentNotated<TItem>[];
  }
};

/**
 * Util for getting an array of parent objects from a flat array of objects
 *
 * DOES NOT REMOVE DUPLICATES, if items are many to many
 *
 * @param target - the item whose parents you would like to find
 * @param items - the array of objects to search through for the parents
 * @param parentKey - the key of the item's parent's ID, defaults to 'parentId'
 * @param idKey - the key of the item's ID, defaults to 'id'
 * @param parentIdList - an array to add the results to, defaults to `[]`
 * @returns a flat array of all the item's ancestors
 */
export const getAncestors = <
  TItem extends Record<KId, TId>,
  TId extends string | number = string,
  KParent extends string = 'parentId',
  KId extends string = 'id'
>(
  target: ParentNotated<TItem, KParent>,
  items: ParentNotated<TItem, KParent>[],
  parentKey: KParent = 'parentId' as KParent,
  idKey: KId = 'id' as KId,
  parentList: TItem[] = []
) => {
  const parentId: TId | null = target[parentKey];
  if (!parentId) {
    return parentList;
  }

  const itemParent = items.find((item) => item[idKey] === parentId);
  if (!itemParent) {
    console.error(`Parent of target with ID: ${target[idKey]} not found`);
    return parentList;
  }
  parentList.push(itemParent);
  getAncestors<TItem, TId, KParent, KId>(itemParent, items, parentKey, idKey, parentList);
  return parentList;
};

export const getAncestorIds = <
  TItem extends Record<KId, TId>,
  TId extends string | number = string,
  KParent extends string = 'parentId',
  KId extends string = 'id'
>(
  target: ParentNotated<TItem, KParent>,
  items: ParentNotated<TItem, KParent>[],
  parentKey: KParent = 'parentId' as KParent,
  idKey: KId = 'id' as KId,
  parentList = []
) => {
  return getAncestors<TItem, TId, KParent, KId>(target, items, parentKey, idKey, parentList).map(
    (ancestor) => ancestor[idKey]
  );
};

/**
 * Util for filtering items from a tree object, or array of tree objects.
 *
 * If you pass a single tree object, not an array, the filter function is not executed on the root level tree object
 *
 * If you pass an array of tree objects, the filter function will execute on the root level tree objects
 *
 * @param tree - either an array of tree objects, or a single tree object
 * @param filter - filter function to execute over the children
 * @param nestingKey - the key of the children, defaults to 'children'
 * @returns the same structure, only containing items that match the filter function
 */
export const filterTree = <
  TItem extends NestedItem<KChildren, KId>,
  KChildren extends string = 'children',
  KId extends string = 'id'
>(
  tree: TItem | TItem[],
  filter: FilterFunction<TItem>,
  nestingKey: KChildren = 'children' as KChildren
) => {
  if (Array.isArray(tree)) {
    const clonedTree = tree.map((item) => ({ ...item }));

    const filtered: any = clonedTree.filter(filter).map((item) => {
      const children = item[nestingKey];
      return {
        ...item,
        // @ts-ignore
        [nestingKey]: children ? filterTree(children, filter, nestingKey) : children,
      };
    });

    return filtered;
  } else {
    const clonedTree = { ...tree };
    if (!clonedTree[nestingKey]) {
      return clonedTree;
    }
    //@ts-ignore
    clonedTree[nestingKey] = clonedTree[nestingKey]
      .filter(
        // @ts-ignore
        filter
      )
      .map((child) => {
        const children = child[nestingKey];
        return {
          ...child,
          [nestingKey]: children ? filterTree(children as any, filter, nestingKey) : children,
        };
      });

    return clonedTree;
  }
};
